import React from 'react';
import {StyleSheet, View, type StyleProp, type ViewStyle} from 'react-native';

import {fonts} from '@/shared/ui/fonts';
import {AppText} from '@/shared/ui/text';
import {radii, useTheme, withAlpha, type Palette} from '@/shared/ui/theme';

/**
 * A chip that labels a thing rather than reporting its live state.
 *
 * `StatusPill` is the sibling and the two are not interchangeable: a pill carries a
 * DOT and one of six state tones, and it means "this is how the thing is right now". A
 * badge carries a word and means "this is what kind of thing it is" — a role, a device
 * type, a plan, a count.
 *
 * Это `Badge` ui-kit: прямоугольный шильдик с тонкой рамкой цветом метки, мягкой
 * заливкой того же тона, радиусом 2pt и 11px подписью. У залитой (`default`) рамки
 * нет — цвет уже занял всю площадь.
 */
export type BadgeVariant =
  | 'default'
  | 'secondary'
  | 'outline'
  | 'info'
  | 'good'
  | 'warn'
  | 'bad'
  | 'muted';

type Skin = {bg: string; fg: string; border: string};

function skin(p: Palette, variant: BadgeVariant): Skin {
  switch (variant) {
    case 'default':
      return {bg: p.primary, fg: p.primaryForeground, border: 'transparent'};
    case 'secondary':
      return {bg: p.neutralSoft, fg: p.textSecondary, border: withAlpha(p.textSecondary, 0.45)};
    case 'outline':
      return {bg: 'transparent', fg: p.foreground, border: p.borderStrong};
    case 'info':
      return {bg: p.infoSoft, fg: p.infoInk, border: withAlpha(p.info, 0.55)};
    case 'good':
      return {bg: p.signalSoft, fg: p.signalInk, border: withAlpha(p.signal, 0.55)};
    case 'warn':
      return {bg: p.warningSoft, fg: p.warningInk, border: withAlpha(p.warning, 0.55)};
    case 'bad':
      return {
        bg: p.destructiveSoft,
        fg: p.destructiveInk,
        border: withAlpha(p.destructive, 0.55),
      };
    case 'muted':
      return {bg: p.neutralSoft, fg: p.mutedForeground, border: p.border};
  }
}

const styles = StyleSheet.create({
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 4,
    minHeight: 20,
    paddingHorizontal: 6,
    borderRadius: radii.xs,
    borderWidth: 1,
  },
  sm: {minHeight: 18, paddingHorizontal: 5, gap: 4},
  label: {fontSize: 11, fontFamily: fonts.sans.regular, lineHeight: 15},
  labelSm: {fontSize: 11, fontFamily: fonts.sans.regular, lineHeight: 15},
});

export function Badge({
  label,
  variant = 'secondary',
  size = 'md',
  /** A glyph or dot before the label. */
  leading,
  style,
  testID,
}: {
  label: string;
  variant?: BadgeVariant;
  size?: 'sm' | 'md';
  leading?: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}) {
  const s = skin(useTheme(), variant);
  const small = size === 'sm';
  return (
    <View
      testID={testID}
      accessibilityRole="text"
      accessibilityLabel={label}
      style={[
        styles.badge,
        small && styles.sm,
        {backgroundColor: s.bg, borderColor: s.border},
        style,
      ]}>
      {leading}
      <AppText color={s.fg} style={small ? styles.labelSm : styles.label} numberOfLines={1}>
        {label}
      </AppText>
    </View>
  );
}

/**
 * A monospace count chip for a panel head or a section title — ui-kit `tabCount`:
 * число на `neutral-soft`, прямоугольное, приглушённое.
 */
export function CountChip({value, style}: {value: number | string; style?: StyleProp<ViewStyle>}) {
  const p = useTheme();
  return (
    <View
      style={[
        {
          paddingHorizontal: 5,
          paddingVertical: 1,
          borderRadius: radii.xs,
          backgroundColor: p.neutralSoft,
        },
        style,
      ]}>
      <AppText variant="mono" color={p.mutedForeground}>
        {value}
      </AppText>
    </View>
  );
}
